import React, { useState, useEffect } from 'react';
import { 
  Save, Edit, Eye, Plus, Trash2, X, Upload, Image, Users, Target, Award, CheckCircle,
  Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  ShoppingBag, Star, Download, Headphones, RefreshCw, BookOpen, AlertCircle
} from 'lucide-react';
import { blogPageAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import AdminSectionWrapper from './AdminSectionWrapper';

const BlogPageManagement = () => {
  const { notify } = useNotificationHook();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [blogData, setBlogData] = useState({
    cta_title: 'Ready to Start Your Project?',
    cta_description: 'Let\'s turn your ideas into reality with our expert development team',
    cta_button1_text: 'Get Started Today',
    cta_button1_link: '/contact',
    cta_button2_text: 'Get Free Quote',
    cta_button2_link: '/quote'
  });

  const [activeSection, setActiveSection] = useState('cta');
  const [isEditing, setIsEditing] = useState(false);

  const sections = [
    { id: 'cta', label: 'Call to Action', icon: CheckCircle }
  ];

  // Fetch blog page data
  useEffect(() => {
    fetchBlogData();
  }, []);

  const fetchBlogData = async () => {
    try {
      setLoading(true);
      const response = await blogPageAPI.getContent();
      if (response?.data?.success) {
        const data = response.data.data;
        setBlogData({
          cta_title: data.cta_title || 'Ready to Start Your Project?',
          cta_description: data.cta_description || 'Let\'s turn your ideas into reality with our expert development team',
          cta_button1_text: data.cta_button1_text || 'Get Started Today',
          cta_button1_link: data.cta_button1_link || '/contact',
          cta_button2_text: data.cta_button2_text || 'Get Free Quote',
          cta_button2_link: data.cta_button2_link || '/quote'
        });
      }
    } catch (err) {
      console.error('Error fetching blog data:', err);
      setError(`Error loading blog data: ${err.message}`);
      notify.error(`Error loading blog data: ${err.message}`, 'Loading Error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setBlogData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveBlogData = async () => {
    try {
      setSaving(true);
      await blogPageAPI.updateContent(blogData);
      setError(null);
      notify.success('Blog page content updated successfully!', 'Success');
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving blog data:', err);
      setError(`Error saving blog data: ${err.message}`);
      notify.error(`Error saving blog data: ${err.message}`, 'Save Error');
    } finally {
      setSaving(false);
    }
  };

  const renderCTASection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Call to Action Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Title
        </label>
        <input
          type="text"
          value={blogData.cta_title}
          onChange={(e) => handleInputChange('cta_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Ready to Start Your Project?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={blogData.cta_description}
          onChange={(e) => handleInputChange('cta_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Let's turn your ideas into reality with our expert development team"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 1 Text
          </label>
          <input
            type="text"
            value={blogData.cta_button1_text}
            onChange={(e) => handleInputChange('cta_button1_text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Get Started Today"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 1 Link
          </label>
          <input
            type="text"
            value={blogData.cta_button1_link}
            onChange={(e) => handleInputChange('cta_button1_link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="/contact"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 2 Text
          </label>
          <input
            type="text"
            value={blogData.cta_button2_text}
            onChange={(e) => handleInputChange('cta_button2_text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Get Free Quote"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 2 Link
          </label>
          <input
            type="text"
            value={blogData.cta_button2_link}
            onChange={(e) => handleInputChange('cta_button2_link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="/quote"
          />
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'cta':
        return renderCTASection();
      default:
        return renderCTASection();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                Blog Page Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Manage blog page content and sections</p>
            </div>
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={saveBlogData}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Content
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 m-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700 dark:text-red-400">{error}</span>
        </div>
      )}

      {/* Content */}
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sections</h2>
            <nav className="space-y-2">
              {sections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6">
          <div className="max-w-4xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </AdminSectionWrapper>
  );
};

export default BlogPageManagement; 